export interface EvaluateExpressionOptions {
  /**
   * If true, unknown identifiers cause an error.
   * If false, unknown identifiers will still cause an error (kept strict for safety).
   */
  strict?: boolean;
}

export interface GeneratePointsRange {
  min: number;
  max: number;
  /**
   * Step in input-space (x or t).
   * If omitted, caller should provide a default.
   */
  step?: number;
}

const allowedIdentifiers = new Set([
  'x',
  't',
  'sin',
  'cos',
  'tan',
  'log',
  'exp',
  'sqrt',
  'abs',
  'min',
  'max',
  'PI',
  'E',
]);

const identifierRegex = /[A-Za-z_][A-Za-z0-9_]*/g;

function getIdentifiers(expr: string): string[] {
  const matches = expr.match(identifierRegex) ?? [];
  return matches;
}

function normalizeExpressionForEvaluation(expr: string): string {
  const trimmed = expr.trim();
  if (trimmed.length === 0) return '';

  // Common normalisations from human/AI formats:
  // - "y = 5x + 10" -> "5x + 10"
  // - "x = t, y = 2t" (parametric parts) -> "t" / "2t"
  const withoutLhs = trimmed.includes('=') ? (trimmed.split('=').pop()?.trim() ?? trimmed) : trimmed;

  // Normalise unicode operators/symbols
  const normalisedSymbols = withoutLhs
    .replace(/[−–—]/g, '-') // minus variants
    .replace(/[×·]/g, '*') // multiplication variants
    .replace(/π/g, 'PI'); // pi symbol

  // Make multiplication explicit for basic cases (e.g. 5x -> 5*x, 2t -> 2*t)
  const explicitMultiply = normalisedSymbols.replace(/(\d)\s*([xt])/g, '$1*$2');

  return explicitMultiply;
}

function assertExpressionSafe(expr: string): void {
  // Hard block characters that allow statement injection / property access tricks.
  // - No semicolons, braces, brackets, backticks
  // - No assignment, no question mark (ternary), no pipe/ampersand, no comparisons
  // - No dot (property access) to avoid Math.constructor, etc.
  const forbiddenChars = /[;{}\[\]`=?:|&<>\\]/;
  if (forbiddenChars.test(expr)) {
    throw new Error('Expression contains forbidden characters');
  }

  // Allow decimal numbers like "0.5" but disallow property access like "Math.sin".
  // Rule: every "." must have a digit on both sides.
  const chars = Array.from(expr);
  const hasInvalidDot = chars.some((ch, i) => {
    if (ch !== '.') return false;
    const prev = chars[i - 1];
    const next = chars[i + 1];
    const isPrevDigit = typeof prev === 'string' && prev >= '0' && prev <= '9';
    const isNextDigit = typeof next === 'string' && next >= '0' && next <= '9';
    return !(isPrevDigit && isNextDigit);
  });
  if (hasInvalidDot) {
    throw new Error('Expression contains invalid "." usage');
  }

  const identifiers = getIdentifiers(expr);
  const invalid = identifiers.filter((id) => !allowedIdentifiers.has(id));
  if (invalid.length > 0) {
    throw new Error(`Expression contains unsupported identifiers: ${Array.from(new Set(invalid)).join(', ')}`);
  }
}

function toJsExpression(expr: string): string {
  // Support ^ as exponent (convert to **)
  // Avoid String.prototype.replaceAll for older TS lib targets
  return expr.replace(/\^/g, '**');
}

export function evaluateExpression(
  expr: string,
  variables: Record<string, number>,
  _options?: EvaluateExpressionOptions,
): number {
  const normalised = normalizeExpressionForEvaluation(expr);
  if (normalised.length === 0) return Number.NaN;

  try {
    assertExpressionSafe(normalised);
  } catch {
    // Never throw during rendering; treat invalid/unsafe expressions as non-renderable.
    return Number.NaN;
  }

  const jsExpr = toJsExpression(normalised);

  const x = variables.x ?? Number.NaN;
  const t = variables.t ?? Number.NaN;

  // Provide whitelisted Math functions/constants as locals (no Math global access).
  const sin = Math.sin;
  const cos = Math.cos;
  const tan = Math.tan;
  const log = Math.log;
  const exp = Math.exp;
  const sqrt = Math.sqrt;
  const abs = Math.abs;
  const min = Math.min;
  const max = Math.max;
  const PI = Math.PI;
  const E = Math.E;

  try {
    const fn = new Function(
      'x',
      't',
      'sin',
      'cos',
      'tan',
      'log',
      'exp',
      'sqrt',
      'abs',
      'min',
      'max',
      'PI',
      'E',
      `"use strict"; return (${jsExpr});`,
    ) as (
      xArg: number,
      tArg: number,
      sinFn: typeof Math.sin,
      cosFn: typeof Math.cos,
      tanFn: typeof Math.tan,
      logFn: typeof Math.log,
      expFn: typeof Math.exp,
      sqrtFn: typeof Math.sqrt,
      absFn: typeof Math.abs,
      minFn: typeof Math.min,
      maxFn: typeof Math.max,
      PIConst: number,
      EConst: number,
    ) => unknown;

    const result = fn(x, t, sin, cos, tan, log, exp, sqrt, abs, min, max, PI, E);
    return typeof result === 'number' ? result : Number.NaN;
  } catch {
    return Number.NaN;
  }
}

export interface Point2D {
  x: number;
  y: number;
}

export type EquationType = 'function' | 'parametric' | 'circle';

export interface CircleParams {
  centerX: number;
  centerY: number;
  radius: number;
}

function splitTopLevelComma(input: string): [string, string] | null {
  const chars = Array.from(input);
  const result = chars.reduce(
    (acc, ch) => {
      const nextDepth = ch === '(' ? acc.depth + 1 : ch === ')' ? Math.max(0, acc.depth - 1) : acc.depth;
      const isSplit = ch === ',' && acc.depth === 0 && acc.splitIndex === -1;
      return {
        depth: nextDepth,
        splitIndex: isSplit ? acc.index : acc.splitIndex,
        index: acc.index + 1,
      };
    },
    {depth: 0, splitIndex: -1, index: 0},
  );

  if (result.splitIndex === -1) return null;
  const left = input.slice(0, result.splitIndex).trim();
  const right = input.slice(result.splitIndex + 1).trim();
  if (!left || !right) return null;
  return [left, right];
}

export function generatePointsFromEquation(params: {
  equation: string;
  equationType: EquationType;
  range: GeneratePointsRange;
  circle?: CircleParams;
}): Point2D[] {
  const {equation, equationType, range, circle} = params;
  const min = range.min;
  const max = range.max;

  const safeStep = range.step && range.step > 0 ? range.step : (max - min) / 200 || 0.05;
  const count = Math.max(2, Math.ceil((max - min) / safeStep) + 1);
  const values = Array.from({length: count}, (_v, i) => min + i * safeStep);

  if (equationType === 'circle') {
    if (!circle) return [];
    const cx = circle.centerX;
    const cy = circle.centerY;
    const r = circle.radius;
    const tValues = Array.from({length: 101}, (_v, i) => (i / 100) * 2 * Math.PI);
    return tValues.map((t) => ({x: cx + r * Math.cos(t), y: cy + r * Math.sin(t)}));
  }

  if (equationType === 'parametric') {
    const parts = splitTopLevelComma(equation);
    if (!parts) return [];
    const [xExpr, yExpr] = parts;
    return values.map((t) => ({
      x: evaluateExpression(xExpr, {t}),
      y: evaluateExpression(yExpr, {t}),
    }));
  }

  // function: y = f(x)
  return values.map((x) => ({x, y: evaluateExpression(equation, {x})}));
}
